export type ChartLevelKind =
    'reference' | 'entry' | 'entry-high' | 'initial' | 'add' | 'average' | 'target' | 'order';

export type ChartLevelStatus =
    'neutral' | 'active' | 'next' | 'planned' | 'complete' | 'closed';

export interface RawChartLevel {
    key: string;
    kind: ChartLevelKind;
    label: string;
    price: number;
    stage: number;
    status: ChartLevelStatus;
}

function number(value: any) {
    const parsed = Number(value || 0);
    return Number.isFinite(parsed) ? parsed : 0;
}

function dateKey(value: any) {
    return String(value || '').slice(0, 10);
}

export function referenceMatchesChart(market: any) {
    const tradingDate = dateKey(market?.trading_date);
    const referenceDate = dateKey(market?.reference_business_date);
    return !!tradingDate && tradingDate === referenceDate;
}

function tickSize(price: number) {
    if (price < 2_000) return 1;
    if (price < 5_000) return 5;
    if (price < 20_000) return 10;
    if (price < 50_000) return 50;
    if (price < 200_000) return 100;
    if (price < 500_000) return 500;
    return 1_000;
}

function floorTick(value: number) {
    const tick = tickSize(Math.max(1, Math.trunc(value)));
    return Math.floor(value / tick) * tick;
}

export function rawChartLevels(
    cycle: any, order: any, market: any, strategy: any
): RawChartLevel[] {
    const levels: RawChartLevel[] = [];
    const previousClose = referenceMatchesChart(market)
        ? number(market?.previous_close) : 0;
    if (previousClose > 0) {
        levels.push({
            key: `reference:${previousClose}`,
            kind: 'reference',
            label: '전일 종가',
            price: previousClose,
            stage: 0,
            status: 'neutral'
        });
    }
    const parameters = strategy?.parameters || {};
    const entryDropPct = number(parameters?.entry_drop_pct);
    if (
        !cycle
        && previousClose > 0
        && parameters?.entry_reference === 'PREVIOUS_CLOSE'
        && entryDropPct > 0
    ) {
        const price = floorTick(previousClose * (1 - entryDropPct / 100));
        levels.push({
            key: `entry:${price}`,
            kind: 'entry',
            label: number(parameters.entry_high_drop_pct) > 0
                ? `전일 종가 -${entryDropPct}%` : `첫 매수 -${entryDropPct}%`,
            price,
            stage: 0,
            status: 'next'
        });
    }
    const dayHigh = referenceMatchesChart(market) ? number(market?.day_high) : 0;
    const highDropPct = number(parameters.entry_high_drop_pct);
    if (!cycle && previousClose > 0 && dayHigh > 0 && highDropPct > 0) {
        const price = floorTick(dayHigh * (1 - highDropPct / 100));
        levels.push({
            key: `entry-high:${price}`, kind: 'entry-high',
            label: `당일 고가 -${highDropPct}%`, price, stage: 0, status: 'next'
        });
    }
    const p1 = number(cycle?.p1);
    if (p1 > 0) {
        levels.push({
            key: `initial:${p1}`,
            kind: 'initial',
            label: '최초 체결',
            price: p1,
            stage: 0,
            status: 'complete'
        });
    }
    const adds = Array.isArray(cycle?.add_levels)
        ? cycle.add_levels : [];
    const addBlocked = ['COMPLETE', 'REVIEW_REQUIRED'].includes(String(cycle?.status || '').toUpperCase());
    const nextAddIndex = addBlocked ? -1 : adds.findIndex((add: any) =>
        ['NEXT', 'WAITING_TIME'].includes(String(add?.status || '').toUpperCase()));
    adds.forEach((add: any, index: number) => {
        if (index !== nextAddIndex) return;
        const price = number(add?.price);
        if (price <= 0) return;
        const stage = Math.max(1, number(add?.stage) || index + 1);
        const rawStatus = String(add?.status || 'PLANNED').toLowerCase();
        const status: ChartLevelStatus = (
            ['next', 'planned', 'complete', 'closed'].includes(rawStatus)
                ? rawStatus : 'planned'
        ) as ChartLevelStatus;
        levels.push({
            key: `add:${stage}:${price}`,
            kind: 'add',
            label: `추가 ${stage}차` + (rawStatus === 'waiting_time' ? ' · 시간 대기' : ''),
            price,
            stage,
            status
        });
    });
    const average = number(cycle?.average_cost);
    if (average > 0) {
        levels.push({
            key: `average:${average}`,
            kind: 'average',
            label: '평단',
            price: average,
            stage: 0,
            status: 'active'
        });
    }
    const frozenTargets = Array.isArray(cycle?.target_levels)
        ? cycle.target_levels : [];
    const previewTargets = Array.isArray(cycle?.target_preview_levels)
        ? cycle.target_preview_levels : [];
    const estimated = frozenTargets.length === 0;
    const targets = cycle?.continuous_add
        ? (cycle?.next_sell ? [cycle.next_sell] : [])
        : estimated ? previewTargets : frozenTargets;
    // Follow the execution stage, not numerical proximity to the current price.
    // Filter before plotting so future/completed targets also leave the axis and legend.
    const nextTargetIndex = targets.findIndex((target: any) =>
        String(target?.status || '').toUpperCase() === 'NEXT');
    targets.forEach((target: any, index: number) => {
        if (index !== nextTargetIndex) return;
        const price = number(target?.price);
        if (price <= 0) return;
        const stage = Math.max(1, number(target?.stage) || index + 1);
        const rawStatus = String(target?.status || 'PLANNED').toLowerCase();
        const status: ChartLevelStatus = (
            ['next', 'planned', 'complete'].includes(rawStatus)
                ? rawStatus : 'planned'
        ) as ChartLevelStatus;
        levels.push({
            key: `target:${stage}:${price}`,
            kind: 'target',
            label: target?.label || (estimated ? `${stage}차 예상 목표` : `${stage}차 목표`),
            price,
            stage,
            status
        });
    });
    const orderPrice = number(order?.limit_price);
    if (orderPrice > 0) {
        const orderStatus = String(order?.status || '').toUpperCase();
        const orderLabel = orderStatus === 'UNKNOWN'
            ? 'KIS 주문 확인 필요'
            : orderStatus === 'SUBMITTING' || orderStatus === 'RESERVED'
                ? '주문 접수 중'
                : orderStatus === 'CANCEL_PENDING'
                    ? '취소 확인 중'
                    : order?.side === 'SELL' ? '미체결 매도' : '미체결 매수';
        levels.push({
            key: `order:${order?.side || ''}:${orderPrice}`,
            kind: 'order',
            label: orderLabel,
            price: orderPrice,
            stage: 0,
            status: 'active'
        });
    }
    return levels;
}
